import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Theme } from "@mui/material/styles";
import { Box, Button, IconButton, Menu, SxProps, Typography } from "@mui/material";
import { DragEvent, MouseEvent, useEffect, useState } from "react";
import { colors, alphaVar } from "../../../ui/colors";
import { ArchiveIconAvatar, fetchMockAvatars, mockAvatars } from "./ArchiveIconsAvatar";
import { CompanyAvatarView } from "./CompanyAvatarView";

type LibraryIconsAvatarProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  disabled: boolean;
  onChangeAvatar: () => void;
  onDropAvatar: (file: File) => void;
  onSelectAvatar: (avatar: ArchiveIconAvatar) => void;
  hasPendingAvatar: boolean;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
  avatarSrc?: string;
  avatarInitial: string;
  avatarAlt: string;
  companyName: string;
  companyRole?: string;
  sx?: SxProps<Theme>;
};

const AVATAR_LIMIT = 18;
const AVATAR_CARD_SIZE = 88;

export const LibraryIconsAvatar = ({
  anchorEl,
  open,
  disabled,
  onChangeAvatar,
  onDropAvatar,
  onSelectAvatar,
  hasPendingAvatar,
  isSaving,
  onSave,
  onCancel,
  avatarSrc,
  avatarInitial,
  avatarAlt,
  companyName,
  companyRole,
  sx,
}: LibraryIconsAvatarProps) => {
  const [avatars, setAvatars] = useState(mockAvatars);
  const [page, setPage] = useState(1);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const totalPages = Math.ceil(mockAvatars.length / AVATAR_LIMIT);

  const handleChangeAvatar = () => {
    if (disabled || isSaving) return;
    onChangeAvatar();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || isSaving) return;
    setIsDraggingFile(true);
  };

  const handleDragLeave = () => {
    setIsDraggingFile(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (disabled || isSaving) return;

    const file = event.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;

    onDropAvatar(file);
  };

  useEffect(() => {
    // Aquí después puedes cambiar fetchMockAvatars por tu endpoint real.
    fetchMockAvatars(page, AVATAR_LIMIT).then(setAvatars);
  }, [page]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onCancel}
      sx={{
        zIndex: 40000,
        "& .MuiBackdrop-root, & .MuiBackdrop-root.MuiBackdrop-invisible": {
          zIndex: 0,
          opacity: "1 !important",
          backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.76) !important",
        },
      }}
      anchorReference="none"
      PaperProps={{
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
        },
        onMouseDown: (event: MouseEvent) => {
          event.stopPropagation();
        },
        sx: {
          zIndex: 1,
          position: "fixed !important",
          top: "50% !important",
          left: "50% !important",
          transform: "translate(-50%, -50%) !important",
          m: 0,
          width: 820,
          height: "auto",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 96px)",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          margin: "auto",
          borderRadius: "12px",
          px: 2,
          pt: 1,
          pb: 2,

          ...sx,
        },
      }}
      MenuListProps={{
        sx: {
          height: "100%",
          p: 0,
        },
      }}
    >
      <IconButton
        aria-label="Cerrar biblioteca"
        onClick={onCancel}
        sx={{
          position: "absolute",
          top: 16,
          right: 18,
          width: 30,
          height: 30,
          bgcolor: "var(--fx-colors-info-100, #DBEAFE)",
          color: "#5f91ad",
          zIndex: 2,
          "&:hover": {
            bgcolor: "#b9ddf3",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Box
        sx={{
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          borderRadius: "8px",
          px: 2,
        }}
      >
        <Typography
          sx={{
            color: "#344955",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Change company logo
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "auto",
          mt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Área de preview y carga */}
        <Box
          sx={{
            width: "100%",
            flex: "0 0 auto",
            p: 2,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
            transition: "0.2s",
            boxShadow: "none",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "132px 1fr",
              gap: 2,
              alignItems: "stretch",
            }}
          >
            {/* Preview del avatar */}
            <Box
              sx={{
                minHeight: 96,
                border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
                borderRadius: "12px",
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                px: 1,
              }}
            >
              <CompanyAvatarView src={avatarSrc} initial={avatarInitial} size={64} alt={avatarAlt} />
              <Box sx={{ width: "100%", minWidth: 0, textAlign: "center" }}>
                <Typography
                  noWrap
                  sx={{
                    color: colors.gray.base,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  {companyName}
                </Typography>
                {!!companyRole && (
                  <Typography
                    noWrap
                    sx={{
                      color: colors.orange.base,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {companyRole}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Área para subir icono */}
            <Box
              onClick={handleChangeAvatar}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 96,
                border: `1px dashed ${isDraggingFile ? colors.orange.base : colors.gray[200]}`,
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                color: colors.gray[500],
                backgroundColor: isDraggingFile ? alphaVar(colors.orange.base, 0.06) : "var(--fx-surface-base, #FFFFFF)",
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                cursor: "pointer",
                "&:hover": {
                  borderColor: colors.orange.base,
                  backgroundColor: alphaVar(colors.orange.base, 0.04),
                },
              }}
            >
              <AddIcon sx={{ fontSize: 26, color: colors.gray[400] }} />
              <Typography
                sx={{
                  color: colors.gray[600],
                  fontSize: 13,
                  fontWeight: 300,
                  textAlign: "center",
                }}
              >
                Drag and drop your icon
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Área de tarjetas de avatar */}
        <Box
          sx={{
            width: "100%",
            flex: "0 0 auto",
            minHeight: 0,
            p: 2,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
            transition: "0.2s",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            "&:hover": {
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(6, ${AVATAR_CARD_SIZE}px)`,
              justifyContent: "space-between",
              alignContent: "start",
              gap: 1.5,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {avatars.slice(0, AVATAR_LIMIT).map((avatar) => (
              <Box
                key={avatar.id}
                onClick={() => {
                  if (disabled || isSaving) return;
                  onSelectAvatar(avatar);
                }}
                sx={{
                  width: AVATAR_CARD_SIZE,
                  aspectRatio: "1 / 1",
                  borderRadius: "12px",
                  border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: colors.orange.base,
                    backgroundColor: alphaVar(colors.orange.base, 0.04),
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={avatar.src}
                  alt={avatar.name}
                  sx={{
                    width: "68%",
                    height: "68%",
                    borderRadius: "10px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Footer con paginación y botones */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 1,
              pt: 0.5,
            }}
          >
            <Box />

            {/* Cambiador de página */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <IconButton
                size="small"
                aria-label="Página anterior"
                disabled={page === 1}
                onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 1))}
                sx={{
                  color: colors.gray[300],
                  "&:hover": {
                    color: colors.orange.base,
                    bgcolor: "transparent",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: colors.orange.base }} />

              <IconButton
                size="small"
                aria-label="Página siguiente"
                disabled={page === totalPages}
                onClick={() => setPage((currentPage) => Math.min(currentPage + 1, totalPages))}
                sx={{
                  color: colors.gray[300],
                  "&:hover": {
                    color: colors.orange.base,
                    bgcolor: "transparent",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Botones para cancelar o guardar el avatar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                onClick={onCancel}
                disabled={isSaving}
                sx={{
                  color: colors.gray[600],
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2.5,
                  "&:hover": {
                    bgcolor: colors.gray[50],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={!hasPendingAvatar || isSaving}
                variant="contained"
                sx={{
                  bgcolor: colors.orange.base,
                  borderRadius: "8px",
                  boxShadow: "none",
                  px: 2.75,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: colors.orange[600],
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    bgcolor: colors.gray[100],
                    color: colors.gray[400],
                  },
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Menu>
  );
};
